<script lang="ts" setup>
import { computed, ref } from 'vue';
import { useMutation } from '@vue/apollo-composable';
import { CodeUpdateDocument, CodeType, OrganizationType } from '@/gql/graphql';
import {
  BAlert,
  BButton,
  BFormGroup,
  BFormInput,
  BFormTextarea,
  BModal,
  BSpinner,
} from 'bootstrap-vue-next';
import OrganizationPicker from '@/components/organization/OrganizationPicker.vue';

// --- Component properties ---
const props = defineProps<{
  code: CodeType;
  show: boolean;
}>();

// --- Component emits ---
const emits = defineEmits(['close', 'closeAndRefresh']);

// --- Component state ---
const show = computed<boolean>(() => {
  return props.show;
});

const newOrderRef = ref<string | null>(props.code.orderRef || null);
const newComment = ref<string | null>(props.code.comment || null);
const newOrganization = ref<OrganizationType | null>(
  props.code.organization || null
);

// --- Mutation ---
const mutationInProgress = ref<boolean>(false);
const mutationError = ref<string | null>(null);
const mutationShowSuccess = ref<boolean>(false);

const {
  mutate: executeMutation,
  onDone: onMutationDone,
  onError: onMutationError,
} = useMutation(CodeUpdateDocument);

onMutationDone((res) => {
  mutationInProgress.value = false;
  if (res.data && res.data.codeUpdate) {
    console.debug('Code updated:', res.data.codeUpdate);
    mutationShowSuccess.value = true;
    setTimeout(() => {
      mutationShowSuccess.value = false;
      emits('closeAndRefresh');
    }, 1000);
  } else {
    mutationError.value = 'Code update failed: ' + res.data;
    console.error('Code update failed:', res.data);
  }
});
onMutationError((err) => {
  console.error('Error updating code:', err);
  mutationInProgress.value = false;
  mutationError.value = err.message;
});

// --- Handlers ---
function close() {
  emits('close');
}

function updateRequested() {
  console.debug('Update requested');

  if (!props.code.id) {
    mutationError.value = 'Code ID is missing';
    console.error('Code ID is missing');
    return;
  }

  mutationInProgress.value = true;
  mutationError.value = null;
  executeMutation({
    id: props.code.id,
    orderRef: newOrderRef.value,
    comment: newComment.value,
    organizationId: newOrganization.value?.id || null,
  });
}
</script>
<template>
  <div>
    <BModal v-model="show" @hide="close">
      <!-- Modal Header -->
      <template #title>
        <font-awesome-icon icon="barcode" class="me-2" />
        {{ code.code?.toUpperCase() }} coupon
      </template>

      <!-- Modal Body-->
      <div>
        <!-- Organization Association -->
        <OrganizationPicker
          class="mt-3"
          v-model="newOrganization"
          label="Organization"
          :allowNullOrganization="true"
          :includeOwnerFullName="true"
          :includeOwnerEmail="true"
          :includeCountryCode="true"
        />

        <!-- Order ref -->
        <BFormGroup
          label="Order reference"
          label-for="new-order-ref"
          class="mt-3"
        >
          <BFormInput
            v-model="newOrderRef"
            id="new-order-ref"
            name="new-order-ref"
            type="text"
            placeholder="Enter your custom order reference"
          ></BFormInput>
        </BFormGroup>

        <!-- Comment -->
        <BFormGroup label="Comment" label-for="new-comment" class="mt-3">
          <BFormTextarea
            v-model="newComment"
            id="new-comment"
            name="new-comment"
            placeholder="Enter a new comment"
          ></BFormTextarea>
        </BFormGroup>

        <!-- Error Message -->
        <b-alert
          v-model="mutationError"
          variant="danger"
          dismissible
          class="mt-3"
          @dismissed="mutationError = null"
        >
          {{ mutationError }}
        </b-alert>
      </div>

      <!-- Modal Footer -->
      <template #footer>
        <BButton @click="close" variant="secondary"> Close </BButton>
        <BButton
          @click="updateRequested"
          :disabled="mutationInProgress || mutationShowSuccess"
          variant="primary"
        >
          <span v-if="mutationInProgress">
            Updating Code
            <b-spinner small label=""></b-spinner>
          </span>
          <span v-else-if="mutationShowSuccess">
            Code Updated
            <font-awesome-icon :icon="['fa', 'check-double']" class="me-2" />
          </span>
          <span v-else> Update Code </span>
        </BButton>
      </template>
    </BModal>
  </div>
</template>
